import React, { useState } from 'react'

import {
  ConfirmationPopoverContent,
  Popover,
  type PopoverProps,
  Tooltip,
  useMediaQuery,
} from '../../../module'
import { type ButtonProps } from '../Button.models'

export type ConfirmationItem = {
  /** The type of confirmation. This will set the color and the icon of the confirmation. */
  type: 'green' | 'red' | 'blue' | 'black'
  /** The text that will appear in the header. */
  header: string
  /** The text that will appear in the body. */
  body: React.ReactNode
  /** The text that will appear for the Confirmation button. By default, it will say `Confirm`. */
  confirmButtonText?: string
  /** The text that will appear for the Cancel button. By default, it will say `Cancel`. */
  cancelButtonText?: string
  /** The callout function for the Confirmation button. */
  confirmCallout: () => void
  /** The callout function for the Cancel button. */
  cancelCallout?: () => void | undefined
}

export type ButtonConfirmationPopoverProps = Omit<
  PopoverProps,
  'popoverContent' | 'children' | 'appendTo' | 'onClickOutside'
>

export type ConfirmationButtonProps = {
  /** This prop will handle displaying the confirmation */
  confirmation: ConfirmationItem
  /** Props received from Button. */
  buttonProps: Pick<
    ButtonProps,
    'disabled' | 'children' | 'className' | 'tooltip' | 'as'
  >
  /** Optionally pass in style */
  style?: React.CSSProperties
  /** Props for the Popover */
  popoverProps?: ButtonConfirmationPopoverProps
  /** Optional tooltip */
  tooltip?: ButtonProps['tooltip']
  /** Optional prop to add a test id to the ConfirmationButton for QA testing */
  qaTestId?: string
}

const ConfirmationButton = ({
  buttonProps,
  popoverProps,
  confirmation,
  tooltip,
  style,
  qaTestId = 'confirmation-button',
}: ConfirmationButtonProps): React.JSX.Element => {
  const screenIsMdMin = useMediaQuery({ type: 'min', breakpoint: 'md' }),
    popoverPosition = popoverProps?.position ?? 'left'
  const [isTooltipOpen, setIsTooltipOpen] = useState(true)

  const arrowColor =
    popoverPosition.includes('bottom') ||
    (popoverPosition.includes('start') && !popoverPosition.includes('top'))
  return (
    <Popover
      {...popoverProps}
      popoverContent={({ setVisible }) => (
        <ConfirmationPopoverContent
          body={confirmation.body ?? ''}
          header={confirmation.header ?? ''}
          type={confirmation.type ?? 'green'}
          cancelCallout={() => {
            confirmation.cancelCallout?.()
            setVisible(false)
          }}
          confirmCallout={() => {
            confirmation.confirmCallout()
            setVisible(false)
          }}
          confirmButtonText={confirmation.confirmButtonText}
          cancelButtonText={confirmation.cancelButtonText}
          qaTestId={`${qaTestId}-popover-content`}
        />
      )}
      position={screenIsMdMin ? popoverPosition : 'bottom'}
      noPadding
      {...(arrowColor
        ? {
            arrowColor:
              `dark-${confirmation.type}` as PopoverProps['arrowColor'],
          }
        : {})}
      className={`${popoverProps?.className ? popoverProps?.className : ''}`}
      qaTestId={`${qaTestId}-popover`}
    >
      {({ setVisible, visible }) => {
        const button = (
          <button
            {...buttonProps}
            style={style}
            onClick={() => {
              setVisible(!visible)
              setIsTooltipOpen(false)
            }}
            onMouseOver={() => {
              !visible && setIsTooltipOpen(true)
            }}
          >
            {buttonProps.children}
          </button>
        )
        return isTooltipOpen && tooltip ? (
          <Tooltip
            tooltipContent={tooltip?.tooltipContent}
            position={tooltip?.position ?? 'bottom'}
          >
            {button}
          </Tooltip>
        ) : (
          button
        )
      }}
    </Popover>
  )
}

export default ConfirmationButton
